import { observer } from 'mobx-react-lite'
import React, { Fragment, useContext, useMemo, useState } from 'react'
import { Coin, Cursor, Display, Flag, GraphUp, Instagram, Megaphone, PieChart, Tiktok, Youtube, Linkedin, Pinterest, Headphones, Airplane, People } from 'react-bootstrap-icons'
import { SharedCampaignContext } from '../../../../../contexts/Advertiser/SharedCampaignContext'
import { currencyFormat, separateThousands } from '../../../../../utils/Helper'
import DropdownButton from '../../../../FormComponents/DropdownButton'

export default observer(function Overview({ campaignScenario }) {
  const { campaign, campaignReport } = useContext(SharedCampaignContext)
  const [open, setOpen] = useState(true)

  const actions = campaignScenario.actions.filter(action => {
    // Check if the action's campaign scenario action group is visible
    const actionGroup = campaignScenario.campaign_scenario_action_groups.find(
      ag => ag.id === action.campaign_scenario_action_group_id
    )
    return actionGroup ? actionGroup.visible_in_budget : true
  })

  const instagramReelsCount = useMemo(() => actions.filter(action => action.action_type === 'instagram_reel').length, [actions])
  const instagramStoriesCount = useMemo(() => actions.filter(action => action.action_type === 'instagram_story').length, [actions])
  const instagramPostsCount = useMemo(() => actions.filter(action => action.action_type === 'instagram_post').length, [actions])
  const instagramCarouselPostsCount = useMemo(() => actions.filter(action => action.action_type === 'instagram_carousel_post').length, [actions])
  const tiktokVideosCount = useMemo(() => actions.filter(action => action.action_type === 'tiktok_video').length, [actions])
  const youtubeVideosCount = useMemo(() => actions.filter(action => action.action_type === 'youtube_video').length, [actions])
  const youtubeShortsCount = useMemo(() => actions.filter(action => action.action_type === 'youtube_short').length, [actions])
  const linkedinBeitragCount = useMemo(() => actions.filter(action => action.action_type === 'linkedin_post').length, [actions])
  const linkedinNewsletterCount = useMemo(() => actions.filter(action => action.action_type === 'linkedin_newsletter').length, [actions])
  const linkedinVideoCount = useMemo(() => actions.filter(action => action.action_type === 'linkedin_video').length, [actions])
  const pinterestPinCount = useMemo(() => actions.filter(action => action.action_type === 'pinterest_pin').length, [actions])
  const podcastEpisodeCount = useMemo(() => actions.filter(action => action.action_type === 'podcast_episode').length, [actions])

  const parseTarget = (v) => {
    if (v == null || v === '') return 0
    return Number(String(v).replace(',', '.')) || 0
  }

  const totalAdvertiserPrice = useMemo(() => actions.reduce((result, action) => result + (Number(action.totalAdvertiserPrice) || 0), 0), [actions])
  const socialAdsSpend = useMemo(() => Number(campaignReport?.paid_spend_overview) || 0, [campaignReport?.paid_spend_overview])
  const targetBudget = useMemo(
    () => parseTarget(campaign?.influencer_budget) + parseTarget(campaign?.media_budget),
    [campaign?.influencer_budget, campaign?.media_budget]
  )
  const totalReach = useMemo(() => actions.reduce((result, action) => result + action.reach, 0), [actions])
  const cpm = useMemo(() => totalReach ? (totalAdvertiserPrice / totalReach) * 1000 : 0, [totalAdvertiserPrice, totalReach])
  const targetCpm = useMemo(() => {
    const target = Number(campaign?.gross_reach_target)
    if (!Number.isFinite(target) || target <= 0) return 0
    const price = Number(totalAdvertiserPrice)
    if (!Number.isFinite(price)) return 0
    return (price / target) * 1000
  }, [totalAdvertiserPrice, campaign?.gross_reach_target])

  const safeCurrency = (value, symbol) => currencyFormat((Number.isFinite(Number(value)) ? Number(value) : 0).toFixed(2), symbol)

  const ADVERTISER_FEE_RATE = 0.20
  const KSA_RATE = 0.049

  // Optional cost widgets (Travel Expenses / Event Attendance): "current" sums the matching
  // system-key Sonstiges field's entries across all influencers/slots for this scenario.
  const systemCostFieldTotal = (systemKey) => {
    const field = (campaign?.custom_fields || []).find(f => f.system_key === systemKey)
    if (!field) return 0
    const records = (campaignScenario?.campaign_custom_field_values || []).filter(
      v => Number(v.campaign_custom_field_id) === Number(field.id)
    )
    return records.reduce((sum, record) => {
      const raw = (Number(record?.quantity) || 0) * (Number(record?.unit_price) || 0)
      const fee = field.advertiser_fee_enabled ? raw * ADVERTISER_FEE_RATE : 0
      const ksa = field.ksa_enabled ? raw * KSA_RATE : 0
      return sum + raw + fee + ksa
    }, 0)
  }

  const travelCostTarget = parseTarget(campaign?.travel_cost_target)
  const travelCostCurrent = useMemo(
    () => systemCostFieldTotal('travel_expenses'),
    [campaign?.custom_fields, campaignScenario?.campaign_custom_field_values]
  )
  const attendanceCostTarget = parseTarget(campaign?.attendance_cost_target)
  const attendanceCostCurrent = useMemo(
    () => systemCostFieldTotal('event_attendance'),
    [campaign?.custom_fields, campaignScenario?.campaign_custom_field_values]
  )

  const calculateColor = (current, target, greenPositive = true) => {
    const t = Number(target)
    const c = Number(current)
    if (!Number.isFinite(c)) return 'text-gray-400'
    if (!Number.isFinite(t) || t === 0) return 'text-gray-400'
    const difference = Math.abs(t - c)
    if ((difference / t) * 100 < 10) return 'text-amber-500'
    return greenPositive ? (t < c ? 'text-rb-green' : 'text-red-500') : (t > c ? 'text-rb-green' : 'text-red-500')
  }

  const reachColor = calculateColor(Number(totalReach), parseTarget(campaign?.gross_reach_target))
  const infPayoutBudgetColor = calculateColor(Number(totalAdvertiserPrice), targetBudget, false)
  const cpmColor = calculateColor(Number(cpm), Number(targetCpm), false)
  const socialBudgetColor = calculateColor(socialAdsSpend, parseTarget(campaign?.social_budget), false)
  const travelCostColor = calculateColor(travelCostCurrent, travelCostTarget, false)
  const attendanceCostColor = calculateColor(attendanceCostCurrent, attendanceCostTarget, false)

  const scopeMetrics = useMemo(() => [
    { icon: Instagram, iconClass: 'text-pink-600', label: 'Instagram Reel', count: instagramReelsCount },
    { icon: Instagram, iconClass: 'text-pink-600', label: 'Instagram Post', count: instagramPostsCount },
    { icon: Instagram, iconClass: 'text-pink-600', label: 'Instagram Carousel Post', count: instagramCarouselPostsCount },
    { icon: Instagram, iconClass: 'text-pink-600', label: 'Instagram Story', count: instagramStoriesCount },
    { icon: Tiktok, iconClass: 'dark:text-white', label: 'TikTok Video', count: tiktokVideosCount },
    { icon: Youtube, iconClass: 'text-red-500', label: 'YouTube Video', count: youtubeVideosCount },
    { icon: Youtube, iconClass: 'text-red-500', label: 'YouTube Shorts', count: youtubeShortsCount },
    { icon: Linkedin, iconClass: 'text-rb-linkedin', label: 'LinkedIn Beitrag', count: linkedinBeitragCount },
    { icon: Linkedin, iconClass: 'text-rb-linkedin', label: 'LinkedIn Newsletter', count: linkedinNewsletterCount },
    { icon: Linkedin, iconClass: 'text-rb-linkedin', label: 'LinkedIn Video', count: linkedinVideoCount },
    { icon: Pinterest, iconClass: 'text-red-700', label: 'Pinterest Pin', count: pinterestPinCount },
    { icon: Headphones, iconClass: 'dark:text-white', label: 'Podcast Episode', count: podcastEpisodeCount },
  ].filter(({ count }) => count > 0), [
    instagramReelsCount, instagramPostsCount, instagramCarouselPostsCount, instagramStoriesCount,
    tiktokVideosCount, youtubeVideosCount, youtubeShortsCount,
    linkedinBeitragCount, linkedinNewsletterCount, linkedinVideoCount, pinterestPinCount, podcastEpisodeCount,
  ])

  return (
    <Fragment>
      { campaignScenario &&
        <div className="bg-white dark:bg-rb-dark-mode rounded-lg p-4 border-1 border-border-primary dark:border-rb-dark-mode-lighter">
          <div className="flex justify-between w-full mb-4">
            <p className="font-bold text-xl dark:text-white">Overview</p>
            <div>
              <DropdownButton dropdownOpen={open} setDropdownOpen={setOpen} className="hover:bg-gray-100 dark:hover:bg-rb-dark-mode-lighter text-black dark:text-white transition-all p-2 rounded-md" />
            </div>
          </div>
          {open &&
            <div>
              <div className="border-1 rounded-xl border-border-primary dark:border-rb-dark-mode-lighter mb-4 p-4">
                <div className="flex items-center text-gray-400 dark:text-gray-400">
                  <Megaphone />
                  <p className="text-rb-lighter-grey dark:text-gray-400 ml-2 mb-1">Scope</p>
                </div>
                {scopeMetrics.length > 0 ? (
                  <div
                    className="gap-4"
                    style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))' }}
                  >
                    {scopeMetrics.map(({ icon: Icon, iconClass, label, count }) => (
                      <div
                        key={label}
                        className="flex justify-between border-1 rounded-lg border-border-primary dark:border-rb-dark-mode-lighter p-2 min-w-0"
                      >
                        <div className="flex items-center min-w-0">
                          <Icon className={`shrink-0 ${iconClass}`} />
                          <p className="ml-2 text-rb-mat-grey dark:text-gray-400 truncate">{label}</p>
                        </div>
                        <p className="font-bold dark:text-white shrink-0 ml-1">{count}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-rb-lighter-grey dark:text-gray-400 text-sm">No actions added to this campaign yet.</p>
                )}
              </div>
              <div className="border-1 rounded-xl border-border-primary dark:border-rb-dark-mode-lighter mb-4 p-4">
                <div className="flex items-center text-gray-400 dark:text-gray-400">
                  <PieChart />
                  <p className="text-rb-lighter-grey dark:text-gray-400 ml-2 mb-1">Overall Cost Overview</p>
                </div>
                <div className="flex justify-between w-full border-1 rounded-lg border-border-primary dark:border-rb-dark-mode-lighter p-2 ">
                  <div className="flex items-center">
                    <p className="ml-2 text-rb-mat-grey dark:text-gray-400">Total Price</p>
                  </div>
                  <p className="font-bold dark:text-white">{safeCurrency(totalAdvertiserPrice, campaign.currency_symbol)}</p>
                </div>
              </div>
              <div className="border-1 rounded-xl border-border-primary dark:border-rb-dark-mode-lighter mb-4 p-4">
                <div className="flex items-center text-gray-400 dark:text-gray-400 justify-between">
                  <div className="flex items-center">
                    <Flag />
                    <p className="text-rb-lighter-grey dark:text-gray-400 ml-2 mb-1">Target and Status</p>
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-4">
                  <div className="rounded-lg border-1 border-border-primary dark:border-rb-dark-mode-lighter p-2">
                    <div className="flex items-center mb-4">
                      <GraphUp className="mr-2 dark:text-white"/>
                      <p className="font-bold dark:text-white">Gross Reach</p>
                    </div>
                    <div className="flex justify-between mb-2">
                      <p className="text-rb-lighter-grey dark:text-gray-400">Target</p>
                      <p className="font-bold dark:text-white">{separateThousands(campaign.gross_reach_target)}</p>
                    </div>
                    <div className="flex justify-between mb-2">
                      <p className="text-rb-lighter-grey dark:text-gray-400">Current</p>
                      <p className="font-bold dark:text-white">{separateThousands(totalReach)}</p>
                    </div>
                    <div className="flex justify-between">
                      <p className="text-rb-lighter-grey dark:text-gray-400">Difference</p>
                      <p className={`font-bold ${reachColor}`}>{separateThousands(Math.abs(Number(totalReach) - parseTarget(campaign?.gross_reach_target)))}</p>
                    </div>
                  </div>
                  <div className="rounded-lg border-1 border-border-primary dark:border-rb-dark-mode-lighter p-2">
                    <div className="flex items-center mb-4">
                      <Coin className="mr-2 dark:text-white"/>
                      <span className="font-bold text-md dark:text-white">Influencer Payout & Buyouts</span>
                      <span className="font-bold text-xs dark:text-white ml-2">(incl. fees)</span>
                    </div>
                    <div className="flex justify-between mb-2">
                      <p className="text-rb-lighter-grey dark:text-gray-400">Target</p>
                      <p className="font-bold dark:text-white text-right">{safeCurrency(targetBudget, campaign.currency_symbol)}</p>
                    </div>
                    <div className="flex justify-between mb-2">
                      <p className="text-rb-lighter-grey dark:text-gray-400">Current</p>
                      <p className="font-bold dark:text-white text-right">{safeCurrency(totalAdvertiserPrice, campaign.currency_symbol)}</p>
                    </div>
                    <div className="flex justify-between">
                      <p className="text-rb-lighter-grey dark:text-gray-400">Difference</p>
                      <p className={`font-bold ${infPayoutBudgetColor} text-right`}>{safeCurrency(Math.abs(targetBudget - totalAdvertiserPrice), campaign.currency_symbol)}</p>
                    </div>
                  </div>
                  {campaign.travel_cost_enabled && (
                    <div className="rounded-lg border-1 border-border-primary dark:border-rb-dark-mode-lighter p-2">
                      <div className="flex items-center mb-4">
                        <Airplane className="mr-2 dark:text-white"/>
                        <p className="font-bold dark:text-white">Travel Expenses</p>
                      </div>
                      <div className="flex justify-between mb-2">
                        <p className="text-rb-lighter-grey dark:text-gray-400">Target</p>
                        <p className="font-bold dark:text-white text-right">{safeCurrency(travelCostTarget, campaign.currency_symbol)}</p>
                      </div>
                      <div className="flex justify-between mb-2">
                        <p className="text-rb-lighter-grey dark:text-gray-400">Current</p>
                        <p className="font-bold dark:text-white text-right">{safeCurrency(travelCostCurrent, campaign.currency_symbol)}</p>
                      </div>
                      <div className="flex justify-between">
                        <p className="text-rb-lighter-grey dark:text-gray-400">Difference</p>
                        <p className={`font-bold ${travelCostColor} text-right`}>{safeCurrency(Math.abs(travelCostTarget - travelCostCurrent), campaign.currency_symbol)}</p>
                      </div>
                    </div>
                  )}
                  {campaign.attendance_cost_enabled && (
                    <div className="rounded-lg border-1 border-border-primary dark:border-rb-dark-mode-lighter p-2">
                      <div className="flex items-center mb-4">
                        <People className="mr-2 dark:text-white"/>
                        <p className="font-bold dark:text-white">Event Attendance</p>
                      </div>
                      <div className="flex justify-between mb-2">
                        <p className="text-rb-lighter-grey dark:text-gray-400">Target</p>
                        <p className="font-bold dark:text-white text-right">{safeCurrency(attendanceCostTarget, campaign.currency_symbol)}</p>
                      </div>
                      <div className="flex justify-between mb-2">
                        <p className="text-rb-lighter-grey dark:text-gray-400">Current</p>
                        <p className="font-bold dark:text-white text-right">{safeCurrency(attendanceCostCurrent, campaign.currency_symbol)}</p>
                      </div>
                      <div className="flex justify-between">
                        <p className="text-rb-lighter-grey dark:text-gray-400">Difference</p>
                        <p className={`font-bold ${attendanceCostColor} text-right`}>{safeCurrency(Math.abs(attendanceCostTarget - attendanceCostCurrent), campaign.currency_symbol)}</p>
                      </div>
                    </div>
                  )}
                  <div className="rounded-lg border-1 border-border-primary dark:border-rb-dark-mode-lighter p-2">
                    <div className="flex items-center mb-4">
                      <Cursor className="mr-2 dark:text-white"/>
                      <p className="font-bold dark:text-white">CPM</p>
                    </div>
                    <div className="flex justify-between mb-2">
                      <p className="text-rb-lighter-grey dark:text-gray-400">Target</p>
                      <p className="font-bold dark:text-white">{safeCurrency(targetCpm, campaign.currency_symbol)}</p>
                    </div>
                    <div className="flex justify-between mb-2">
                      <p className="text-rb-lighter-grey dark:text-gray-400">Current</p>
                      <p className="font-bold dark:text-white">{safeCurrency(cpm, campaign.currency_symbol)}</p>
                    </div>
                    <div className="flex justify-between">
                      <p className="text-rb-lighter-grey dark:text-gray-400">Difference</p>
                      <p className={`font-bold ${cpmColor}`}>{safeCurrency(Math.abs((cpm || 0) - (targetCpm || 0)), campaign.currency_symbol)}</p>
                    </div>
                  </div>
                  <div className="rounded-lg border-1 border-border-primary dark:border-rb-dark-mode-lighter p-2">
                    <div className="flex items-center mb-4">
                      <Display className="mr-2 dark:text-white"/>
                      <p className="font-bold dark:text-white">Social Paid Budget</p>
                    </div>
                    <div className="flex justify-between mb-2">
                      <p className="text-rb-lighter-grey dark:text-gray-400">Target</p>
                      <p className="font-bold dark:text-white">{safeCurrency(parseTarget(campaign?.social_budget), campaign.currency_symbol)}</p>
                    </div>
                    <div className="flex justify-between mb-2">
                      <p className="text-rb-lighter-grey dark:text-gray-400">Current</p>
                      <p className="font-bold dark:text-white">{safeCurrency(socialAdsSpend, campaign.currency_symbol)}</p>
                    </div>
                    <div className="flex justify-between">
                      <p className="text-rb-lighter-grey dark:text-gray-400">Difference</p>
                      <p className={`font-bold ${socialBudgetColor}`}>{safeCurrency(Math.abs(socialAdsSpend - parseTarget(campaign?.social_budget)), campaign.currency_symbol)}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          }
        </div>
      }
    </Fragment>
  )
})
